import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Pencil, Camera, Trash2, MessageCircle, FileDown,
  CalendarDays, Wallet, History, User, Lock, RotateCcw, RotateCw, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { JaritBadge } from "@/components/JaritBadge";
import { EmployeeForm } from "@/components/EmployeeForm";
import { useAuth } from "@/context/AuthContext";
import { rp } from "@/lib/payroll";
import { bankColor } from "@/lib/bank";
import { useT } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function waLink(no) {
  let d = (no || "").replace(/\D/g, "");
  if (!d) return null;
  if (d.startsWith("620")) d = "62" + d.slice(3);
  else if (d.startsWith("0")) d = "62" + d.slice(1);
  else if (!d.startsWith("62")) d = "62" + d;
  if (d.length < 9) return null;
  return `https://wa.me/${d}`;
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
};

const ageFrom = (iso) => {
  if (!iso) return null;
  const b = new Date(iso + "T00:00:00");
  if (Number.isNaN(b.getTime())) return null;
  const n = new Date();
  let a = n.getFullYear() - b.getFullYear();
  const m = n.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && n.getDate() < b.getDate())) a -= 1;
  return a >= 0 && a < 130 ? a : null;
};

const Avatar = ({ emp, version, size = "h-24 w-24" }) => {
  const [failed, setFailed] = useState(false);
  const show = emp?.has_photo && !failed;
  const src = show
    ? `${BACKEND}/api/employees/${emp.id}/photo?v=${version}`
    : "/default-avatar.webp";
  return (
    <div className={`${size} shrink-0 overflow-hidden rounded-full bg-white ring-1 ring-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.08)]`}>
      <img
        data-testid={show ? "profile-photo-img" : "profile-photo-default"}
        src={src}
        alt={emp?.nama || "Employee"}
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

const Row = ({ label, children, testid }) => (
  <div className="flex flex-col gap-0.5 border-b border-black/[0.05] py-2.5 last:border-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
    <span className="shrink-0 text-[13px] text-[#8E8E93]">{label}</span>
    <span data-testid={testid} className="break-words text-[14px] font-medium text-[#1D1D1F] sm:max-w-[62%] sm:text-right">{children}</span>
  </div>
);

const Card = ({ title, children, right, className = "" }) => (
  <div className={`card-soft p-5 ${className || "!rounded-[40px]"}`}>
    {title && (
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[13px] font-semibold uppercase tracking-wider text-[#A1A1A6]">{title}</h3>
        {right}
      </div>
    )}
    {children}
  </div>
);

const StatCard = ({ label, value, tone = "gray", testid }) => {
  const tones = {
    green: "bg-[#E9F9EF] text-[#0E8A46]", amber: "bg-[#FFF1E6] text-[#B5590F]",
    red: "bg-[#FDECEC] text-[#C0362C]", blue: "bg-[#EEF3FA] text-[#0071E3]",
    gray: "bg-[#F2F2F7] text-[#1D1D1F]",
  };
  return (
    <div className={`rounded-2xl px-4 py-3 ${tones[tone]}`} data-testid={testid}>
      <p className="text-[22px] font-semibold leading-tight">{value}</p>
      <p className="text-[12px] font-medium opacity-80">{label}</p>
    </div>
  );
};

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useT();
  const isAdmin = user?.role === "admin" || user?.role === "viewer";
  const readOnly = user?.role === "viewer";

  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [photoVer, setPhotoVer] = useState(Date.now());
  const [access, setAccess] = useState(null);
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banks, setBanks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const fileRef = useRef(null);

  const [bulan, setBulan] = useState(10);
  const [tahun, setTahun] = useState(2026);

  const canEdit = (isAdmin && !readOnly) || access?.state === "approved";

  const loadEmp = useCallback(async () => {
    try {
      const { data } = await api.get(`/employees/${id}/profile`);
      setEmp(data);
    } catch (e) {
      toast.error(errMsg(e));
      navigate("/karyawan");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadAccess = useCallback(() => {
    if (isAdmin) return;
    api.get("/employee-access").then((r) => setAccess(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { loadEmp(); loadAccess(); }, [loadEmp, loadAccess]);

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data || [])).catch(() => {});
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, []);

  // Deep-link: ?edit=1 opens edit mode (used by the Employees list edit button)
  useEffect(() => {
    if (searchParams.get("edit") === "1" && canEdit) {
      setEditing(true);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, canEdit, setSearchParams]);

  if (loading) {
    return <div className="flex items-center gap-2 py-24 text-[14px] text-[#8E8E93]"><Loader2 className="h-4 w-4 animate-spin" /> {t("prof.loading")}</div>;
  }
  if (!emp) return null;

  const uploadPhoto = async (f) => {
    if (!f) return;
    if (!canEdit) { toast.error(t("prof.toastNeedPerm")); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/employees/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("prof.toastPhotoUpdated"));
      setPhotoVer(Date.now());
      await loadEmp();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removePhoto = async () => {
    try {
      await api.delete(`/employees/${id}/photo`);
      toast.success(t("prof.toastPhotoRemoved"));
      setPhotoVer(Date.now());
      await loadEmp();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const requestAccess = async () => {
    try { await api.post("/employee-access/request"); toast.success(t("prof.toastReqSent")); loadAccess(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveEdit = async (payload, fingerspotPin) => {
    setBusy(true);
    try {
      await api.put(`/employees/${emp.id}`, payload);
      if (isAdmin && (fingerspotPin || "") !== (emp.fingerspot_pin || "")) {
        try { await api.put(`/employees/${emp.id}/fingerspot-pin`, { pin: fingerspotPin }); }
        catch (fe) { toast.error(errMsg(fe)); }
      }
      toast.success(t("prof.toastSaved"));
      setEditing(false);
      await loadEmp();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const tabs = [
    { k: "overview", label: t("prof.tabOverview"), icon: User },
    { k: "attendance", label: t("prof.tabAttendance"), icon: CalendarDays },
    { k: "kasbon", label: t("prof.tabKasbon"), icon: Wallet },
    { k: "potongan", label: t("prof.tabPotongan"), icon: Receipt },
    ...(isAdmin ? [{ k: "salary", label: t("prof.tabSalary"), icon: History }] : []),
  ];

  return (
    <div data-testid="employee-profile" className="space-y-5">
      <button data-testid="profile-back" onClick={() => navigate("/karyawan")} className="flex items-center gap-1.5 text-[13.5px] font-medium text-[#8E8E93] transition-colors hover:text-[#1D1D1F]">
        <ArrowLeft className="h-4 w-4" /> {t("prof.backEmployees")}
      </button>

      {!isAdmin && access && access.state !== "approved" && (
        <div className="flex flex-col gap-3 rounded-2xl border border-white/60 bg-white/60 p-4 shadow-sm backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-[13px] text-[#3A3A3C]"><Lock className="h-4 w-4 text-[#0071E3]" />
            {access.state === "pending" ? t("prof.accessPending") : t("prof.accessReadonly")}</p>
          {access.state !== "pending" && (
            <button data-testid="profile-request-edit" onClick={requestAccess} className="inline-flex h-10 items-center gap-2 rounded-full bg-[#0071E3] px-4 text-[13.5px] font-semibold text-white active:scale-95">
              <Lock className="h-4 w-4" /> {t("prof.requestEdit")}
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <Card className="!rounded-[48px]">
        <div className="relative flex items-center gap-4 sm:gap-6">
          {canEdit && !editing && (
            <div className="absolute right-0 top-0">
              <button data-testid="profile-actions-btn" onClick={() => setMenuOpen((v) => !v)} title={t("prof.editEmployee")}
                className="grid h-9 w-9 place-items-center rounded-full border border-[#E1E1E6] bg-white/70 text-[#0071E3] transition-all hover:bg-white active:scale-95">
                <Pencil className="h-4 w-4" />
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="profile-photo-input"
                onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
                  <div data-testid="profile-actions-menu" className="absolute right-0 top-11 z-30 w-52 overflow-hidden rounded-2xl border border-black/[0.06] bg-white/95 p-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.16)] backdrop-blur-xl">
                    <button data-testid="profile-edit-btn" onClick={() => { setEditing(true); setMenuOpen(false); }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13.5px] font-medium text-[#1D1D1F] hover:bg-[#F2F2F7]">
                      <Pencil className="h-4 w-4 text-[#0071E3]" /> {t("prof.editDetails")}
                    </button>
                    <button data-testid="profile-photo-upload" onClick={() => { fileRef.current?.click(); setMenuOpen(false); }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13.5px] font-medium text-[#1D1D1F] hover:bg-[#F2F2F7]">
                      <Camera className="h-4 w-4 text-[#0071E3]" /> {emp.has_photo ? t("prof.replacePhoto") : t("prof.uploadPhoto")}
                    </button>
                    {emp.has_photo && (
                      <div onClick={() => setMenuOpen(false)}>
                        <AdjustPhoto emp={emp} version={photoVer} onSaved={() => { setPhotoVer(Date.now()); return loadEmp(); }} menuItem />
                      </div>
                    )}
                    {emp.has_photo && (
                      <button data-testid="profile-photo-remove" onClick={() => { removePhoto(); setMenuOpen(false); }}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13.5px] font-medium text-[#C0362C] hover:bg-[#FDECEC]">
                        <Trash2 className="h-4 w-4" /> {t("prof.removePhoto")}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="shrink-0">
            <Avatar emp={emp} version={photoVer} size="h-16 w-16 sm:h-24 sm:w-24" />
          </div>
          <div className="min-w-0 flex-1 pr-11">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#A1A1A6]">{t("prof.eyebrow")}</p>
            <h1 data-testid="profile-name" className="flex min-w-0 items-center gap-1.5 text-[18px] font-semibold tracking-tight sm:gap-2 sm:text-[28px]">
              <span className="truncate">{emp.nama}</span>
              {emp.verified && !(emp.is_jarit || emp.payroll_type === "PIECE_RATE") && <VerifiedBadge size={22} className="shrink-0" title="Verified employee" />}
              <JaritBadge show={emp.is_jarit || emp.payroll_type === "PIECE_RATE"} size={22} />
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[13px]">
              <span className="num font-medium text-[#0071E3]">{emp.emp_code || "—"}</span>
              <span className="text-[#C7C7CC]">·</span>
              <span className="text-[#6E6E73]">{emp.jabatan || "—"}</span>
            </div>
          </div>
        </div>
      </Card>

      {editing ? (
        <div data-testid="profile-edit-card" className="card-soft !rounded-[40px] p-4 sm:p-6">
          <h3 className="mb-4 text-[13px] font-semibold uppercase tracking-wider text-[#A1A1A6]">{t("prof.editTitle")}</h3>
          <EmployeeForm initial={emp} isAdmin={isAdmin} banks={banks} employees={employees} busy={busy} onSubmit={saveEdit} onCancel={() => setEditing(false)} />
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 py-2">
            {tabs.map((t) => (
              <button key={t.k} data-testid={`profile-tab-${t.k}`} onClick={() => setTab(t.k)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-[13.5px] font-medium transition-all ${tab === t.k ? "bg-[#0071E3] text-white shadow-[0_2px_8px_rgba(0,113,227,0.25)]" : "bg-white/70 text-[#6E6E73] hover:bg-white"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "overview" && <Overview emp={emp} isAdmin={isAdmin} />}
          {tab === "attendance" && <Attendance emp={emp} isAdmin={isAdmin} bulan={bulan} tahun={tahun} setBulan={setBulan} setTahun={setTahun} navigate={navigate} />}
          {tab === "kasbon" && <Kasbon emp={emp} navigate={navigate} />}
          {tab === "potongan" && <PotonganHistory emp={emp} />}
          {tab === "salary" && isAdmin && <SalaryHistory emp={emp} />}
        </>
      )}
    </div>
  );
}

function MonthYear({ bulan, tahun, setBulan, setTahun }) {
  const { months } = useT();
  return (
    <div className="flex gap-2">
      <Select value={String(bulan)} onValueChange={(v) => setBulan(Number(v))}>
        <SelectTrigger data-testid="profile-month" className="h-10 w-[140px] rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]"><SelectValue /></SelectTrigger>
        <SelectContent className="rounded-xl">{months.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(tahun)} onValueChange={(v) => setTahun(Number(v))}>
        <SelectTrigger data-testid="profile-year" className="h-10 w-[96px] rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]"><SelectValue /></SelectTrigger>
        <SelectContent className="rounded-xl">{[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function Overview({ emp, isAdmin }) {
  const { t } = useT();
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card title={t("prof.personalInfo")}>
        <Row label={t("prof.fullName")} testid="ov-nama">{emp.nama}</Row>
        <Row label={t("prof.nipCode")}>{emp.emp_code || "—"}</Row>
        <Row label={t("prof.nikId")}>{emp.nik || "—"}</Row>
        <Row label={t("prof.birthDate")} testid="ov-birthday">{fmtDate(emp.tanggal_lahir)}{ageFrom(emp.tanggal_lahir) != null ? <span className="ml-1.5 text-[#8E8E93]">({t("prof.yearsOld", { n: ageFrom(emp.tanggal_lahir) })})</span> : null}</Row>
        <Row label={t("prof.position")}>{emp.jabatan || "—"}</Row>
        <Row label={t("prof.yearJoined")} testid="ov-tahun-masuk">{emp.tahun_masuk || "—"}</Row>
        <Row label={t("prof.phone")}>{emp.no_hp ? (waLink(emp.no_hp) ? <a data-testid="ov-phone-wa" href={waLink(emp.no_hp)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-medium text-[#128C3E] hover:underline">{emp.no_hp}<MessageCircle className="h-3.5 w-3.5" /></a> : emp.no_hp) : "—"}</Row>
        <Row label={t("prof.status")}>{emp.aktif ? t("prof.active") : t("prof.inactive")}</Row>
      </Card>
      <Card title={t("prof.payrollEmployment")}>
        <Row label={t("prof.payrollType")}>{emp.payroll_type === "PIECE_RATE" ? t("prof.typePiece") : t("prof.typeDaily")}</Row>
        {isAdmin && emp.payroll_type !== "PIECE_RATE" && <Row label={t("prof.ratePerDay")} testid="ov-rate">{rp(emp.rate_harian || 0)}</Row>}
        <Row label="BPJS Ketenagakerjaan">{emp.bpjs ? t("prof.yes") : t("prof.no")}</Row>
        {isAdmin && <Row label={t("prof.autoBonus")}>{emp.eligible_bonus ? t("prof.bonusAuto") : t("prof.bonusExcluded")}</Row>}
      </Card>
      <Card title={t("prof.bankInfo")}>
        <Row label={t("prof.payment")}>{emp.metode === "transfer" ? t("prof.transfer") : t("prof.cash")}</Row>
        {emp.metode === "transfer" && <>
          <Row label={t("prof.bank")}><span className="font-semibold" style={{ color: bankColor(emp.bank) }}>{(emp.bank || "—").toUpperCase()}</span></Row>
          <Row label={t("prof.accountNo")}>{emp.no_rekening || "—"}</Row>
          <Row label={t("prof.accountName")}>{emp.atas_nama || "—"}</Row>
        </>}
      </Card>
      <Card title={t("prof.attendance")}>
        {isAdmin && <Row label="Fingerspot PIN">{emp.fingerspot_pin || t("prof.notLinked")}</Row>}
        <Row label={t("prof.attSource")}>{emp.payroll_type === "PIECE_RATE" ? t("prof.naBorongan") : "Fingerspot"}</Row>
      </Card>
    </div>
  );
}

function Attendance({ emp, isAdmin, bulan, tahun, setBulan, setTahun, navigate }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/employees/${emp.id}/attendance/month?bulan=${bulan}&tahun=${tahun}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [emp.id, bulan, tahun]);
  const s = data?.summary || {};
  const pdfUrl = `${BACKEND}/api/attendance/pdf/monthly?bulan=${bulan}&tahun=${tahun}&employee_id=${emp.id}`;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MonthYear bulan={bulan} tahun={tahun} setBulan={setBulan} setTahun={setTahun} />
        <div className="flex gap-2">
          {isAdmin && emp.payroll_type !== "PIECE_RATE" && (
            <a data-testid="att-export-pdf" href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex h-10 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3.5 text-[13px] font-medium text-[#0071E3]"><FileDown className="h-4 w-4" /> {t("prof.exportPdf")}</a>
          )}
          <button data-testid="att-view-full" onClick={() => navigate(isAdmin ? "/attendance/fingerprint" : "/absensi")} className="inline-flex h-10 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3.5 text-[13px] font-medium text-[#6E6E73]"><CalendarDays className="h-4 w-4" /> {t("prof.fullAttendance")}</button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-10 text-[13px] text-[#8E8E93]"><Loader2 className="h-4 w-4 animate-spin" /> {t("prof.loading")}</div>
      ) : (
        <>
          {emp.payroll_type === "PIECE_RATE" && (
            <Card><p className="py-3 text-center text-[13px] text-[#8E8E93]">{t("prof.pieceNote")}</p></Card>
          )}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-5">
            <StatCard testid="att-full" label={t("prof.statFull")} value={s.full ?? 0} tone="green" />
            <StatCard testid="att-half" label={t("prof.statHalf")} value={s.half ?? 0} tone="blue" />
            <StatCard testid="att-zero" label={t("prof.statZero")} value={s.zero ?? 0} tone="red" />
            <StatCard testid="att-needs" label={t("prof.statNeeds")} value={s.needs_review ?? 0} tone="amber" />
            <StatCard testid="att-total" label={t("prof.statTotalVal")} value={s.total_value ?? "0"} tone="gray" />
          </div>
          {(data?.days || []).length === 0 ? (
            <Card><p className="py-6 text-center text-[13.5px] text-[#8E8E93]">{t("prof.noAttendance")}</p></Card>
          ) : (
            <Card>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-left text-[13.5px]">
                  <thead><tr className="border-b border-black/[0.06] text-[10.5px] uppercase tracking-wider text-[#A1A1A6]">
                    {[t("prof.colDate"), t("prof.colDay"), t("prof.colIn"), t("prof.colOut"), t("prof.colResult"), t("prof.colValue"), t("prof.colNote")].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {data.days.map((d, i) => (
                      <tr key={i} data-testid={`att-day-${i}`} className="border-b border-black/[0.04] last:border-0">
                        <td className="num px-3 py-2.5">{d.tanggal}</td><td className="px-3 py-2.5 text-[#6E6E73]">{d.hari}</td>
                        <td className="num px-3 py-2.5">{d.masuk || "—"}</td><td className="num px-3 py-2.5">{d.pulang || "—"}</td>
                        <td className="px-3 py-2.5 font-medium">{d.status}</td><td className="num px-3 py-2.5">{d.nilai}</td>
                        <td className="px-3 py-2.5 text-[12px] text-[#8E8E93]">{d.ket || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="space-y-2 md:hidden">
                {data.days.map((d, i) => (
                  <div key={i} data-testid={`att-day-m-${i}`} className="rounded-xl bg-[#FAFAFA] p-3">
                    <div className="flex items-center justify-between"><span className="num text-[13px] font-semibold">{d.tanggal} · {d.hari}</span><span className="rounded-full bg-white px-2 py-0.5 text-[11.5px] font-medium">{d.status} · {d.nilai}</span></div>
                    <p className="num mt-1 text-[12.5px] text-[#6E6E73]">{d.masuk || "—"} → {d.pulang || "—"}</p>
                    {d.ket && <p className="mt-0.5 text-[11.5px] text-[#8E8E93]">{d.ket}</p>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Kasbon({ emp, navigate }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("all");
  const [bal, setBal] = useState(null);
  useEffect(() => {
    setLoading(true);
    api.get(`/employees/${emp.id}/kasbon-history`)
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
    api.get(`/kasbon/${emp.id}`).then((r) => setBal(r.data)).catch(() => {});
  }, [emp.id]);
  const years = useMemo(() => {
    const ys = Array.from(new Set((data?.records || []).map((r) => r.tahun))).sort((a, b) => b - a);
    return ys;
  }, [data]);
  const shown = (data?.records || []).filter((r) => year === "all" || r.tahun === Number(year));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {years.length > 1 && (
            <Select value={String(year)} onValueChange={setYear}>
              <SelectTrigger data-testid="kasbon-year-filter" className="h-10 w-[150px] rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">{t("prof.allHistory")}</SelectItem>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <button data-testid="kasbon-manage" onClick={() => navigate(`/kasbon/${emp.id}`)} className="inline-flex h-10 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3.5 text-[13px] font-medium text-[#0071E3]"><Wallet className="h-4 w-4" /> {t("prof.kasbonNote")}</button>
      </div>
      {bal && (
        <button data-testid="kasbon-sisa-card" onClick={() => navigate(`/kasbon/${emp.id}`)} className="card-soft lift flex w-full items-center justify-between p-5 text-left">
          <div>
            <p className="text-[13px] font-medium text-[#8E8E93]">{t("prof.kasbonBalance")}</p>
            <p className="num mt-1 text-[26px] font-semibold tracking-tight" style={{ color: bal.balance > 0 ? "#C0362C" : "#0E8A46" }}>Rp {rp(bal.balance)}</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] text-[#8E8E93]">{t("prof.kasbonTotal")} Rp {rp(bal.total_charge)}</p>
            <p className="text-[11px] text-[#8E8E93]">{t("prof.kasbonDeducted")} Rp {rp(bal.total_payment)}</p>
          </div>
        </button>
      )}
      {loading ? (
        <div className="flex items-center gap-2 py-10 text-[13px] text-[#8E8E93]"><Loader2 className="h-4 w-4 animate-spin" /> {t("prof.loading")}</div>
      ) : (
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[13px] text-[#8E8E93]">{t("prof.kasbonHistory")}</span>
            <span data-testid="kasbon-period-count" className="rounded-full bg-[#F2F2F7] px-2.5 py-0.5 text-[12px] font-medium text-[#6E6E73]">
              {shown.length} {shown.length === 1 ? t("prof.period") : t("prof.periods")}
            </span>
          </div>
          {shown.length > 0 ? (
            <div className="space-y-2">
              {shown.map((r, i) => (
                <div key={i} data-testid={`kasbon-hist-${i}`} className="flex items-center justify-between rounded-xl bg-[#FAFAFA] px-3.5 py-3">
                  <div className="min-w-0">
                    <p className="text-[14px] font-medium text-[#1D1D1F]">{r.label}</p>
                    <p className="text-[11.5px] text-[#8E8E93]">
                      {r.source}{r.entry_date ? ` · ${r.entry_date}` : ""}{r.entered_by ? ` · ${t("prof.by")} ${r.entered_by}` : ""}
                    </p>
                    {r.note && <p className="mt-0.5 text-[12px] text-[#6E6E73]">{r.note}</p>}
                  </div>
                  <span className="num shrink-0 text-[15px] font-semibold">{rp(r.amount)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p data-testid="kasbon-empty" className="py-6 text-center text-[13.5px] text-[#8E8E93]">{t("prof.kasbonEmpty")}</p>
          )}
        </Card>
      )}
    </div>
  );
}

function PotonganHistory({ emp }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => {
    api.get(`/employees/${emp.id}/potongan-history`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [emp.id]);
  if (data === null) return <div className="flex items-center gap-2 py-10 text-[13px] text-[#8E8E93]"><Loader2 className="h-4 w-4 animate-spin" /> {t("prof.loading")}</div>;
  const records = data.records || [];
  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[13px] text-[#8E8E93]">{t("prof.potonganTitle")}</span>
        <span className="rounded-full bg-[#F2F2F7] px-2.5 py-0.5 text-[12px] font-medium text-[#6E6E73]">{records.length} {records.length === 1 ? t("prof.period") : t("prof.periods")}</span>
      </div>
      {records.length === 0 ? (
        <p data-testid="potongan-empty" className="py-6 text-center text-[13.5px] text-[#8E8E93]">{t("prof.potonganEmpty")}</p>
      ) : (
        <div className="space-y-2">
          {records.map((r, i) => {
            const isOpen = !!open[i];
            return (
              <div key={i} data-testid={`potongan-hist-${i}`} className="rounded-xl bg-[#FAFAFA]">
                <button onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))} className="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left">
                  <div className="min-w-0">
                    <p className="text-[14px] font-medium text-[#1D1D1F]">{r.label}</p>
                    <p className="text-[11.5px] text-[#8E8E93]">{t("prof.itemsTapDetail", { n: r.items.length })}</p>
                  </div>
                  <span className="num shrink-0 text-[15px] font-semibold text-[#C0362C]">- {rp(r.total)}</span>
                </button>
                {isOpen && (
                  <div className="space-y-1.5 border-t border-black/[0.05] px-3.5 py-2.5">
                    {r.items.map((it, j) => (
                      <div key={j} className="flex items-center justify-between gap-3 text-[13px]">
                        <span className="min-w-0 truncate text-[#3A3A3C]">{it.keterangan}</span>
                        <span className="num shrink-0 font-medium text-[#1D1D1F]">{rp(it.nominal)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function SalaryHistory({ emp }) {
  const { t } = useT();
  const [periods, setPeriods] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  useEffect(() => {
    api.get(`/employees/${emp.id}/salary-history`).then((r) => setPeriods(r.data.periods || [])).catch((e) => toast.error(errMsg(e)));
  }, [emp.id]);
  const openDetail = async (pid) => {
    setLoadingDetail(true); setDetail({ open: true });
    try { const { data } = await api.get(`/employees/${emp.id}/salary-history/${pid}`); setDetail({ open: true, ...data }); }
    catch (e) { toast.error(errMsg(e)); setDetail(null); }
    finally { setLoadingDetail(false); }
  };
  if (periods === null) return <div className="flex items-center gap-2 py-10 text-[13px] text-[#8E8E93]"><Loader2 className="h-4 w-4 animate-spin" /> {t("prof.loading")}</div>;
  if (periods.length === 0) return <Card><p className="py-6 text-center text-[13.5px] text-[#8E8E93]">{t("prof.salaryEmpty")}</p></Card>;
  return (
    <div className="space-y-2.5">
      {periods.map((p) => (
        <button key={p.period_id} data-testid={`salary-period-${p.bulan}-${p.tahun}`} onClick={() => openDetail(p.period_id)}
          className="card-soft flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-[#FAFAFA]">
          <div>
            <p className="text-[14.5px] font-semibold">{p.label}</p>
            <p className="num text-[12px] text-[#8E8E93]">{p.is_jarit ? t("prof.borongan") : t("prof.days", { n: p.hari_kerja })} · Kasbon {rp(p.kasbon)}</p>
          </div>
          <div className="text-right">
            <p className="num text-[16px] font-semibold text-[#0E8A46]">{rp(p.total)}</p>
            <p className="text-[11px] text-[#8E8E93]">{t("prof.takeHome")}</p>
          </div>
        </button>
      ))}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent data-testid="salary-detail-dialog" className="max-h-[90vh] gap-0 overflow-y-auto rounded-[20px] border border-white/50 bg-white/85 p-6 backdrop-blur-2xl sm:max-w-[460px]">
          <DialogHeader className="text-left"><DialogTitle className="text-[18px] font-semibold">{t("prof.payrollDetail")} — {detail?.label || ""}</DialogTitle></DialogHeader>
          {loadingDetail || !detail?.entry ? (
            <div className="flex items-center gap-2 py-10 text-[13px] text-[#8E8E93]"><Loader2 className="h-4 w-4 animate-spin" /> {t("prof.loading")}</div>
          ) : (
            <div className="mt-3">
              {!detail.entry.is_jarit && <Row label={t("prof.workDays")}>{detail.entry.hari_kerja}</Row>}
              {!detail.entry.is_jarit && <Row label={t("prof.ratePerDay")}>{rp(detail.entry.rate_harian)}</Row>}
              <Row label={t("prof.wage")}>{rp(detail.entry.upah)}</Row>
              <Row label={t("prof.bonus")}>{rp(detail.entry.bonus)}</Row>
              {(detail.entry.pendapatan_items || []).map((it, i) => <Row key={`p${i}`} label={it.keterangan || t("prof.otherIncome")}>{rp(it.nominal)}</Row>)}
              <Row label="BPJS">- {rp(detail.entry.bpjs_potongan)}</Row>
              <Row label={t("prof.tabKasbon")}>- {rp(detail.entry.kasbon)}</Row>
              {(detail.entry.potongan_items || []).map((it, i) => <Row key={`d${i}`} label={it.keterangan || t("prof.otherDeduction")}>- {rp(it.nominal)}</Row>)}
              <div className="mt-3 flex items-center justify-between rounded-xl bg-[#E9F9EF] px-4 py-3">
                <span className="text-[14px] font-semibold text-[#0E8A46]">{t("prof.takeHome")}</span>
                <span data-testid="salary-detail-total" className="num text-[18px] font-semibold text-[#0E8A46]">{rp(detail.entry.total)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdjustPhoto({ emp, version, onSaved, menuItem }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [deg, setDeg] = useState(0);
  const [busy, setBusy] = useState(false);
  const norm = ((deg % 360) + 360) % 360;
  const save = async () => {
    if (!norm) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.post(`/employees/${emp.id}/photo/rotate`, { degrees: norm });
      toast.success(t("prof.toastOrientSaved"));
      setOpen(false); setDeg(0); await onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/employees/${emp.id}/photo/reset`, {});
      toast.success(t("prof.toastRestored"));
      setOpen(false); setDeg(0); await onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="profile-photo-adjust" onClick={() => { setDeg(0); setOpen(true); }}
        className={menuItem
          ? "flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13.5px] font-medium text-[#1D1D1F] hover:bg-[#F2F2F7]"
          : "flex items-center gap-1 rounded-full bg-[#F2F2F7] px-2.5 py-1 text-[11.5px] font-medium text-[#6E6E73]"}>
        <RotateCw className={menuItem ? "h-4 w-4 text-[#0071E3]" : "h-3.5 w-3.5"} /> {menuItem ? t("prof.adjustPhoto") : t("prof.adjust")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="adjust-photo-dialog" className="gap-0 rounded-[20px] border border-white/50 bg-white/90 p-6 backdrop-blur-2xl sm:max-w-[380px]">
          <DialogHeader className="text-left"><DialogTitle className="text-[17px] font-semibold">{t("prof.adjustPhoto")}</DialogTitle></DialogHeader>
          <div className="grid place-items-center py-5">
            <div className="h-44 w-44 overflow-hidden rounded-full ring-1 ring-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.08)]">
              <img data-testid="adjust-photo-preview" src={`${BACKEND}/api/employees/${emp.id}/photo?v=${version}`} alt={emp.nama} style={{ transform: `rotate(${deg}deg)` }} className="h-full w-full object-cover transition-transform duration-200" />
            </div>
          </div>
          <div className="flex justify-center gap-2">
            <button data-testid="rotate-left" onClick={() => setDeg((d) => d - 90)} className="flex items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#1D1D1F] active:scale-95"><RotateCcw className="h-4 w-4" /> {t("prof.left")}</button>
            <button data-testid="rotate-right" onClick={() => setDeg((d) => d + 90)} className="flex items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#1D1D1F] active:scale-95"><RotateCw className="h-4 w-4" /> {t("prof.right")}</button>
            <button data-testid="rotate-reset-local" onClick={() => setDeg(0)} className="rounded-full border border-[#E1E1E6] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#6E6E73] active:scale-95">{t("prof.reset")}</button>
          </div>
          <div className="mt-5 flex items-center gap-2">
            <button data-testid="rotate-restore-original" onClick={restore} disabled={busy} className="h-10 flex-1 rounded-xl border border-[#E1E1E6] bg-white text-[13px] font-medium text-[#6E6E73] disabled:opacity-50">{t("prof.restoreOriginal")}</button>
            <button data-testid="rotate-save" onClick={save} disabled={busy} className="flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-[#0071E3] text-[14px] font-medium text-white disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("prof.save")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
